import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Shield, CheckCircle, AlertTriangle } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentClaimPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing claim token. Please use the link from your email.");
      setLoading(false);
      return;
    }
    axios.get(`${API}/parent-claim/${token}`)
      .then(r => { setInfo(r.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.detail || "Invalid or expired claim link");
        setLoading(false);
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr(null);
    if (!name.trim()) { setSubmitErr("Please enter your name"); return; }
    if (password.length < 6) { setSubmitErr("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setSubmitErr("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/parent-claim/${token}`, { name: name.trim(), password });
      // Auto-login: store token + user
      if (r.data?.token) {
        localStorage.setItem("ysv_token", r.data.token);
        localStorage.setItem("ysv_user", JSON.stringify(r.data.user));
      }
      setDone(true);
      setTimeout(() => { window.location.href = "/home"; }, 1400);
    } catch (err) {
      setSubmitErr(err.response?.data?.detail || "Failed to claim account");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="parent-claim-page" className="page-gradient" style={{
      paddingTop: 120, minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '120px 24px 80px',
    }}>
      <div style={{ maxWidth: 500, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <YSVLogo size="lg" showSubtext />
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 700, marginTop: 16, marginBottom: 8 }}>Claim Your YSV Account</h1>
        </div>

        {loading && (
          <div className="glass-card" style={{ padding: 32, textAlign: 'center' }}>
            <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div data-testid="claim-error" className="glass-card" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(244,67,54,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <AlertTriangle size={28} style={{ color: '#f44336' }} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Link Issue</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>{error}</p>
            <button onClick={() => navigate("/login")} className="btn-primary" style={{ padding: '10px 22px' }}>Go to Sign In</button>
          </div>
        )}

        {!loading && !error && info && !done && (
          <form data-testid="claim-form" onSubmit={submit} className="glass-card" style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 4 }}>
              <p style={{ fontSize: 13, color: '#fff', fontWeight: 600, marginBottom: 4 }}>
                <strong style={{ color: 'var(--cyan)' }}>{info.child_name || "Your child"}</strong> signed up for YSV
              </p>
              {info.team_name && (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Team: {info.team_name}{info.org_name ? ` · ${info.org_name}` : ""}
                </p>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                Account email: <span data-testid="claim-email" style={{ color: '#fff' }}>{info.parent_email}</span>
              </p>
            </div>

            <input data-testid="claim-name" className="input-field" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} required />
            <input data-testid="claim-password" className="input-field" type="password" placeholder="Choose a password (min 6 chars)" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
            <input data-testid="claim-confirm" className="input-field" type="password" placeholder="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6} />

            {submitErr && <p data-testid="claim-submit-error" style={{ color: '#f44336', fontSize: 13 }}>{submitErr}</p>}

            <button data-testid="claim-submit" type="submit" disabled={submitting} className="btn-primary" style={{ marginTop: 4, opacity: submitting ? 0.7 : 1 }}>
              <Shield size={16} /> {submitting ? "Creating account…" : "Claim My Account"}
            </button>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 4 }}>
              By claiming this account you agree to YSV's terms. Your child's affiliation is auto-linked to you.
            </p>
          </form>
        )}

        {done && (
          <div data-testid="claim-success" className="glass-card" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(76,175,80,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <CheckCircle size={28} style={{ color: '#4caf50' }} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Account claimed!</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Taking you to YSV…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentClaimPage;
